import { useState } from "react";

import { BodySection } from "@/components/ui/BodySection";
import { ViewAllLink } from "@/components/ui/ViewAllLink";
import { InvoicesList } from "@/presentation/money/InvoicesList";
import { PayoutsList } from "@/presentation/money/PayoutsList";
import { TransactionsList } from "@/presentation/money/TransactionsList";
import { merchantTabPath } from "@/routes";

/** Enough to see the shape of recent activity; the tab has the rest. */
const PREVIEW_LIMIT = 5;

const MOVEMENTS = [
  { id: "transactions", label: "Transactions" },
  { id: "payouts", label: "Payouts" },
  { id: "invoices", label: "Invoices" },
] as const;

type Movement = (typeof MOVEMENTS)[number]["id"];

/**
 * Recent money in, out and owed, behind one set of tabs.
 *
 * Only the selected list mounts, so switching costs a query rather than
 * three on load. Each carries its own "View all" to the matching tab, named
 * for what it opens — "View all payouts" says where it goes in a way a bare
 * "View all" repeated three times cannot.
 */
export function MoneyMovementSection({ businessId }: { businessId: string }) {
  const [active, setActive] = useState<Movement>("transactions");

  return (
    <BodySection
      title="Money movement"
      footer={
        <ViewAllLink to={merchantTabPath(businessId, active)}>
          {`View all ${active}`}
        </ViewAllLink>
      }
    >
      <div className="mb-4 flex gap-1 border-b">
        {MOVEMENTS.map((movement) => (
          <button
            key={movement.id}
            type="button"
            onClick={() => setActive(movement.id)}
            className={[
              "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              active === movement.id
                ? "border-blue-600 text-blue-600"
                : "text-muted-foreground hover:text-foreground border-transparent",
            ].join(" ")}
          >
            {movement.label}
          </button>
        ))}
      </div>

      {active === "transactions" && (
        <TransactionsList
          businessId={businessId}
          previewLimit={PREVIEW_LIMIT}
        />
      )}
      {active === "payouts" && (
        <PayoutsList businessId={businessId} previewLimit={PREVIEW_LIMIT} />
      )}
      {active === "invoices" && (
        <InvoicesList businessId={businessId} previewLimit={PREVIEW_LIMIT} />
      )}
    </BodySection>
  );
}
